import { AttachmentFile, SuretyAccountId, SuretyBondId } from "@inrev/common";
import { useBondAttachmentActions } from "@portal/api/attachments";
import { useFetchBondAttachments } from "@portal/api/bond";
import { useFetchAdminContractExtractions } from "@portal/api/bond/admin";
import { useFeatureFlag } from "@portal/hooks/useFeatureFlag";
import { useAuthenticatedUser } from "@portal/providers/AuthenticatedUserProvider";
import { FC, useMemo } from "react";
import { ContractExtraction } from "../../uploaded-documents/types";
import {
	createUploadedDocumentsColumns,
	UPLOADED_DOCUMENTS_INITIAL_SORTING,
} from "../../uploaded-documents/UploadedDocumentsColumns";
import { mapAttachmentsToUploadedDocumentsRow } from "../../uploaded-documents/utils/mapAttachmentsToUploadedDocumentsRow";
import { AttachmentsTable } from "./AttachmentsTable";

const EMPTY_ATTACHMENTS: AttachmentFile[] = [];
const EMPTY_CONTRACT_EXTRACTIONS: ContractExtraction[] = [];

type BondAttachmentsTableProps = {
	bondId: SuretyBondId;
	accountId?: SuretyAccountId;
	filter?: (attachment: AttachmentFile) => boolean;
	downloadAllName: string;
	allowUpload?: boolean;
	allowDelete?: boolean;
};

export const BondAttachmentsTable: FC<BondAttachmentsTableProps> = ({
	bondId,
	accountId,
	filter,
	downloadAllName,
	allowUpload = true,
	allowDelete = true,
}) => {
	const { isAdmin } = useAuthenticatedUser();
	const bondRequestExtractionEnabled = useFeatureFlag("bond_request_extraction");
	const aiBondRequestExtractionEnabled = useFeatureFlag("ai_bond_request_extraction");
	const { attachments, attachmentsIsLoading } = useFetchBondAttachments(bondId);
	const { extractions } = useFetchAdminContractExtractions({
		accountId,
		bondId,
		enabled:
			isAdmin &&
			accountId != null &&
			bondRequestExtractionEnabled &&
			aiBondRequestExtractionEnabled,
	});
	const actionsConfig = useBondAttachmentActions({
		bondId,
		isAdmin,
		allowDelete,
		allowUpload,
	});
	const { downloadBaseUrl, downloadQueryKeyPrefix } = actionsConfig;
	const trackedActionsConfig = useMemo(
		() => ({ ...actionsConfig, analyticsContext: "bond-attachments" as const }),
		[actionsConfig],
	);

	const columns = useMemo(
		() => createUploadedDocumentsColumns({ actions: trackedActionsConfig }),
		[trackedActionsConfig],
	);

	const filteredAttachments = useMemo(
		() =>
			filter
				? (attachments ?? EMPTY_ATTACHMENTS).filter(filter)
				: (attachments ?? EMPTY_ATTACHMENTS),
		[attachments, filter],
	);

	const contractExtractions = extractions ?? EMPTY_CONTRACT_EXTRACTIONS;

	const allUploadedRows = useMemo(
		() => mapAttachmentsToUploadedDocumentsRow(filteredAttachments, [], contractExtractions),
		[filteredAttachments, contractExtractions],
	);

	const downloadConfig = useMemo(
		() =>
			downloadAllName
				? {
						baseUrl: downloadBaseUrl,
						queryKeyPrefix: downloadQueryKeyPrefix,
						fileName: downloadAllName,
					}
				: undefined,
		[downloadBaseUrl, downloadQueryKeyPrefix, downloadAllName],
	);

	return (
		<AttachmentsTable
			tableStorageKey="bond-attachments"
			sectionName="Bond Attachments"
			columns={columns}
			initialSorting={UPLOADED_DOCUMENTS_INITIAL_SORTING}
			isLoading={attachmentsIsLoading}
			downloadConfig={downloadConfig}
			rows={allUploadedRows}
			allowUpload={allowUpload}
			handleUpload={actionsConfig.handleUpload}
			filesPendingUpload={actionsConfig.filesPendingUpload}
			filesCompletedUpload={actionsConfig.filesCompletedUpload}
			analyticsContext="bond-attachments"
			useCardLayout
		/>
	);
};
